/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { elexity } from '../utilities/LobbyKioskGraphHelper';
import configuration from '../config';
import AuthenticatedComponent from '../utilities/AuthenticatedComponent';
import WithRouter from '../utilities/WithRouter';

import KioskModel from '../components/KioskModel';
import {
  kioskCards,
  energyCards,
  co2Cards,
  noModelCards,
  footerCards,
} from '../utilities/LobbyKioskCardHelper';

import elexity_logo from '../assets/elexity_logo_full.svg';
import elexity_logo_dark from '../assets/elexity_logo_full_dark.svg';

import { setCurrentGX, setCurrentGXData } from '../actions';
import { connect } from 'react-redux';

require('highcharts/modules/accessibility');
require('highcharts/modules/draggable-points');
require('highcharts/modules/exporting');
require('highcharts/modules/export-data');

const LobbyKiosk = ({ currentGXData, currentGXId, setCurrentGX, updateGXData }) => {
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const [newGraphConfiguration, setNewGraphConfiguration] = useState(null);

  const mode = searchParams.get('mode');
  const version = searchParams.get('version');
  const liveChartRef = useRef(null);
  const authenticatedComponent = new AuthenticatedComponent();

  if (currentGXData && currentGXData.kiosk && energyCards.length === 0) {
    kioskCards(currentGXData, mode === 'dark');
  }

  //Load initial graphs
  let graphConfiguration = {
    seriesDefinitions: [
      {
        name: 'Grid Power',
        color: '#FA974F',
        type: 'spline',
        opacity: 1,
        dataGrouping: { approximation: 'average' },
      },
      {
        name: 'Total Load',
        color: '#2FE0E6',
        type: 'column',
        opacity: 0.75,
        dataGrouping: { approximation: 'average' },
      },
    ],

    defaultLiveZoom: 3,
  };

  useEffect(() => {
    let tempGX = window.location.pathname.split('/')[3];
    setCurrentGX(window.location.pathname.split('/')[3]);
    updateGXData();

    elexity.admin.monitor.graphs.initAuth(authenticatedComponent.generateAuthenticatedHeader());
    elexity.admin.monitor.graphs.init(
      tempGX,
      configuration['backend_host'] + '/ahiapi/lobbyKiosk/' + tempGX + '/graph/definition',
      configuration['backend_host'] + '/ahiapi/lobbyKiosk/' + tempGX + '/graph',
      graphConfiguration,
      'livePerformanceGraph'
    );
  }, []);

  useEffect(() => {
    localStorage.clear();

    if (searchParams) {
      if (mode === 'dark') {
        localStorage.setItem('theme', 'dark');
        document.documentElement.setAttribute('data-theme', 'dark');
      }

      if (version === 'noModel') {
        localStorage.setItem('version', 'noModel');
        document.documentElement.setAttribute('data-version', 'noModel');
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (newGraphConfiguration !== null && newGraphConfiguration.seriesDefinitions) {
      elexity.admin.monitor.graphs.initAuth(authenticatedComponent.generateAuthenticatedHeader());
      elexity.admin.monitor.graphs.init(
        currentGXId,
        configuration['backend_host'] + '/ahiapi/lobbyKiosk/' + currentGXId + '/graph/definition',
        configuration['backend_host'] + '/ahiapi/lobbyKiosk/' + currentGXId + '/graph',
        newGraphConfiguration,
        'livePerformanceGraph'
      );
    }
  }, [newGraphConfiguration]);

  useEffect(() => {
    if (currentGXData && currentGXData.kiosk) {
      //Show or hide battery, solar, ev based on whether or not it's on a campus
      if (currentGXData.kiosk && newGraphConfiguration === null) {
        if (currentGXData.kiosk) {
          if (currentGXData.kiosk.hasBattery) {
            addSeriesDefinition(true, 'Battery Power', '#02BE7F');
          }
          if (currentGXData.kiosk.hasSolar) {
            addSeriesDefinition(true, 'Solar Power', '#F5D500');
          }
          if (currentGXData.kiosk.hasEvCharger) {
            addSeriesDefinition(true, 'EV Charger Power', '#0FFDC2');
          }
        }
      }

      //Graph data update every 5 seconds
      let interval = setInterval(() => {
        updateGXData();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [currentGXData]);

  function addSeriesDefinition(available, name, color) {
    setNewGraphConfiguration(
      { ...graphConfiguration },
      graphConfiguration.seriesDefinitions.splice(1, 0, {
        name: name,
        color: color,
        type: 'spline',
        opacity: 1,
        dataGrouping: { approximation: 'average' },
        visible: available,
      })
    );
  }

  return (
    <>
      <LobbyKioskPage className={mode === 'dark' ? 'dark lobby-kiosk' : 'lobby-kiosk'}>
        <GraphKiosk>
          {version !== 'noModel' && (
            <div className="kiosk-model content-block">
              <h4>Now</h4>
              {currentGXData && currentGXData.kiosk && (
                <KioskModel
                  gxPower={currentGXData.kiosk.gridkW}
                  hasSolar={currentGXData.kiosk.hasSolar}
                  solarPower={currentGXData.kiosk.solarkW}
                  hasBattery={currentGXData.kiosk.hasBattery}
                  batterySoc={currentGXData.kiosk.batterySoc}
                  batterySockWh={currentGXData.kiosk.batterySockWh}
                  batteryDisplaySoc={currentGXData.kiosk.batteryDisplaySoc}
                  batteryPower={currentGXData.kiosk.batterykW}
                  hasEv={currentGXData.kiosk.hasEvCharger}
                  hasEvConnected={currentGXData.kiosk.hasEvConnected}
                  evPower={currentGXData.kiosk.evChargerkW}
                  hvacPower={currentGXData.kiosk.hvackW}
                  otherPower={currentGXData.kiosk.buildingkW}
                />
              )}
            </div>
          )}

          <div
            className={
              version === 'noModel'
                ? 'energy-insights-graph content-block full-width'
                : 'energy-insights-graph content-block'
            }
          >
            <h4>Energy Insights - last 3 days</h4>
            <div
              id="livePerformanceGraph"
              className="insights-graph-container"
              ref={liveChartRef}
            ></div>
          </div>
        </GraphKiosk>

        <Stats className="content-block">
          <div className="stats-container">
            <h4>Last 30 Days</h4>

            {version !== 'noModel' ? (
              <>
                <div className="energy-stat-container">
                  <h6>Energy</h6>
                  {energyCards}
                </div>

                <hr />

                <div className="co2-stat-container">
                  <h6>
                    CO<sup>2</sup> Equivalencies
                  </h6>
                  {co2Cards}
                </div>
              </>
            ) : (
              <>
                <div className="co2-stat-container">{noModelCards}</div>
              </>
            )}
          </div>
        </Stats>

        <LobbyFooter>
          {version === 'noModel' && (
            <>
              <div className="footer-stats">{footerCards}</div>
            </>
          )}
          <div className="elexity-logo">
            <p>Powered By</p>
            <img src={mode === 'dark' ? elexity_logo_dark : elexity_logo} alt="" />
          </div>
        </LobbyFooter>
      </LobbyKioskPage>
    </>
  );
};

const LobbyKioskPage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 30px 30px 0;
  row-gap: 24px;

  h4,
  h6 {
    color: #02222b;
    margin: 0;
  }

  h4 {
    font-size: 24px;
    font-weight: 800;
    line-height: 22px;
  }

  h6 {
    font-size: 18px;
    font-weight: 600;
    text-decoration: underline;
    text-transform: uppercase;
    width: 215px;
    sup {
      font-size: 14px;
      line-height: 0;
    }
  }

  p {
    margin: 0;
  }

  .label {
    font-size: 18px;
    font-weight: 500;
  }

  .value {
    font-size: 24px;
    font-weight: 600;
  }

  .content-block {
    background: white;
  }

  &.dark {
    h4,
    h6,
    p.label,
    p.value,
    p.soc-value {
      color: white !important;
    }

    .content-block {
      background: rgba(50, 78, 85, 0.35);

      hr {
        background: rgba(255, 255, 255, 0.35);
      }
    }

    .container .arrow {
      &.inactive {
        .arrow-container {
          .line {
            border: 2px dashed white;
          }
          .arrow-icon {
            border-color: white;
          }
        }
      }
    }
  }
`;

const Stats = styled.section`
  background: white;
  border: none;
  border-radius: 8px;
  box-shadow: rgba(194, 208, 209, 0.5) 0 0 10px;
  padding: 20px;

  .stats-container {
    align-items: flex-start;
    color: #02222b;
    row-gap: 24px;
    display: flex;
    flex-flow: column;
  }

  hr {
    background: #edf1f2;
    border: none;
    height: 3px;
    width: 100%;
  }

  .energy-stat-container,
  .co2-stat-container {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
`;

const GraphKiosk = styled.section`
  align-items: center;
  column-gap: 24px;
  display: flex;
  justify-content: space-between;
  width: 100%;
  flex-flow: unset;

  .kiosk-model {
    background: white;
    border: none;
    border-radius: 8px;
    box-shadow: rgba(194, 208, 209, 0.5) 0 0 10px;
    display: flex;
    flex-flow: column;
    height: 520px;
    justify-content: space-between;
    padding: 20px;
    width: 40%;
  }

  .kiosk-container {
    border: 0;
    height: 100%;
    padding: 0;
    width: 100%;

    .container .arrow .arrow-container .line {
      height: 78px;
    }

    .container .block {
      width: unset;
    }
  }

  h6 {
    margin-top: 5px;
  }

  .energy-insights-graph {
    background: rgba(255, 255, 255, 0.5);
    border: none;
    border-radius: 8px;
    box-shadow: rgba(194, 208, 209, 0.5) 0 0 10px;
    display: flex;
    flex-direction: column;
    height: 540px;
    justify-content: space-between;
    padding: 20px 20px 0;
    width: 60%;

    &.full-width {
      height: 600px;
      width: 100%;
    }
  }

  .insights-graph-container {
    height: 500px;
    width: 100%;

    .loading {
      color: #02222b;
      font-size: 18px;
      font-weight: 600;
    }
  }

  .highcharts-background {
    fill: none;
  }
`;

const LobbyFooter = styled.section`
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: space-between;

  .footer-stats {
    align-items: center;
    display: flex;
    gap: 24px;
    .total-energy,
    .battery-soc {
      align-items: center;
      border: none;
      border-radius: 8px;
      box-shadow: rgba(194, 208, 209, 0.5) 0 0 10px;
      display: flex;
      gap: 12px;
      padding: 12px 24px;

      .stats {
        align-items: center;
        display: flex;
        justify-content: space-between;
        width: 400px;
      }
    }
  }

  .elexity-logo {
    align-items: center;
    display: flex;
    justify-content: center;

    p {
      margin: 0;
    }

    img {
      height: 30px;
      padding-left: 20px;
      width: 140px;
    }
  }
`;

const mapStateToProps = (state) => {
  return {
    campuses: state.campuses,
    currentCampusId: state.currentCampusId,
    currentGXId: state.currentGXId,
    currentCampusData: state.currentCampusData,
    currentGXData: state.currentGXData,
  };
};

// used by parent class AuthenticatedComponent
const mapDispatchToProps = (dispatch) => {
  return {
    setCurrentGX: (gxId) => {
      dispatch(setCurrentGX(gxId));
    },
    setCurrentGXData: (gxData) => {
      dispatch(setCurrentGXData(gxData));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithRouter(LobbyKiosk));
